import gql from 'graphql-tag';

const GraphQL = {
  Query: {
    COURSE: gql`
      query Course($coursekey: Int!) {
        course(coursekey: $coursekey) {
          coursekey
          title
          description
          tags
          created
          published
          displaySettings {
            background {
              type
              value
            }
            icon {
              type
              value
            }
            behaviourFlags
            certificate {
              expiry
              media
              lifetime
              expires
            }
          }
          state
        }
      }
    `,
    COURSES: gql`
      query Courses {
        courses {
          coursekey
          title
          description
          created
          published
          displaySettings {
            background {
              type
              value
            }
            icon {
              type
              value
            }
            behaviourFlags
            certificate {
              expiry
              media
              lifetime
              expires
            }
          }
          state
        }
      }
    `,
    COURSE_ALTERED: gql`
      query CourseAltered($coursekey: Int!) {
        courseAltered(coursekey: $coursekey)
      }
    `,
    COURSE_AUTHORS: gql`
      query CourseAuthors($coursekey: Int!) {
        courseAuthors(coursekey: $coursekey) {
          userkey
          username
          firstname
          lastname
          read
          write
          share
        }
      }
    `,
    COURSE_AUTHORS_AVATAR: gql`
      query CourseAuthorsAvatar($coursekey: Int!) {
        courseAuthorsAvatar(coursekey: $coursekey) {
          userkey
          read
          write
          share
        }
      }
    `,
    COURSE_PERMISSION: gql`
      query CoursePermission($coursekey: Int!) {
        coursePermission(coursekey: $coursekey) {
          userkey
          coursekey
          read
          write
          share
        }
      }
    `,
    LESSON: gql`
      query Lesson($lessonkey: Int!) {
        lesson(lessonkey: $lessonkey) {
          coursekey
          lessonkey
          title
          description
          tags
          type
          displaySettings {
            background {
              type
              value
              containerColor
            }
            icon {
              type
              value
            }
            palette {
              main
              dark
              contrastText
            }
          }
          behaviorSettings {
            showAllVersions
            lockCourse
            editAllVersions
            completionType
            completionThreshold
            multipleAttempts
          }
          xApiSettings {
            activityId
            activityName
            launchPage
          }
          certificate {
            expiry
            media
            lifetime
            expires
          }
          version {
            value
            published
            live
            liveVersion
          }
          position
          required
          attempts {
            userkey
            modulekey
            layer
            moduleversion
            state
            status
            created
            modified
            completed
            expires
          }
        }
      }
    `,
    LESSON_VERSION_ASSET: gql`
      query LessonVersionAsset($lessonkey: Int!, $lessonVersion: Int!) {
        lessonVersionAsset(
          lessonkey: $lessonkey
          lessonVersion: $lessonVersion
        ) {
          native {
            completionFlags
            threshold
            singleAttemptPerVersion
          }
          xapi {
            activityId
            activityName
            launchPage
          }
        }
      }
    `,
    ARCHIVED_LESSONS: gql`
      query ArchivedLessons($coursekey: Int!) {
        archivedLessons(coursekey: $coursekey) {
          modulekey
          title
          created
          archived
        }
      }
    `,
    AUTHORS: gql`
      query Authors {
        authors {
          userkey
          username
          firstname
          lastname
          authoraccess
          masteraccess
        }
      }
    `,
    TEMPLATES: gql`
      query Templates {
        templates {
          templatekey
          domainkey
          title
          description
          created
          modified
        }
      }
    `,
    FETCH_LOCK: gql`
      query Lock($modulekey: Int!) {
        lock(modulekey: $modulekey) {
          modulekey
          userkey
          username
          firstname
          lastname
          created
          modified
          expires
        }
      }
    `,
    FETCH_LINKS: gql`
      query Links($modulekey: Int!) {
        links(modulekey: $modulekey) {
          linkkey
          created
          expires
        }
      }
    `,
    COURSE_EDIT_LESSONS: gql`
      query CourseEditLessons($coursekey: Int!) {
        courseEditLessons(coursekey: $coursekey) {
          course {
            title
            description
            published
            displaySettings {
              background {
                type
                value
              }
              icon {
                type
                value
              }
            }
          }
          lessons {
            lessonkey
            title
            type
            position
            required
            liveposition
            liveversion
            liverequired
            availableversion
          }
        }
      }
    `,
    COURSE_EDIT_LESSONS_LIST: gql`
      query CourseEditLessonsList($coursekey: Int!) {
        courseEditLessonsList(coursekey: $coursekey) {
          lessonkey
          title
        }
      }
    `,
    GET_EDIT_LESSON: gql`
      query GetEditLesson($coursekey: Int!, $lessonkey: Int!) {
        getEditLesson(coursekey: $coursekey, lessonkey: $lessonkey) {
          coursekey
          lessonkey
          title
          description
          tags
          type
          displaySettings {
            background {
              type
              value
              containerColor
            }
            icon {
              type
              value
            }
            palette {
              main
              dark
              contrastText
            }
          }
          behaviorSettings {
            showAllVersions
            lockCourse
            editAllVersions
            completionType
            completionThreshold
            multipleAttempts
          }
          xApiSettings {
            activityId
            activityName
            launchPage
          }
          certificate {
            expiry
            media
            lifetime
            expires
          }
          version {
            value
            published
            live
            liveVersion
          }
          # Not emitted.
          # position
          # Not emitted.
          # attempts
        }
      }
    `
  },
  Mutation: {
    CREATE_COURSE: gql`
      mutation CreateCourse(
        $title: String!
        $description: String
        $tags: String
        $authors: [CourseAuthorInput]!
        $displaySettings: CourseDisplaySettingsInput
      ) {
        createCourse(
          title: $title
          description: $description
          tags: $tags
          authors: $authors
          displaySettings: $displaySettings
        ) {
          coursekey
          title
          description
          created
        }
      }
    `,
    CLONE_COURSE: gql`
      mutation CloneCourse($coursekey: Int!, $title: String!) {
        cloneCourse(coursekey: $coursekey, title: $title) {
          coursekey
          title
          description
          created
          published
        }
      }
    `,
    DELETE_COURSE: gql`
      mutation DeleteCourse($coursekey: Int!) {
        deleteCourse(coursekey: $coursekey)
      }
    `,
    UNPUBLISH_COURSE: gql`
      mutation UnpublishCourse($coursekey: Int!) {
        unpublishCourse(coursekey: $coursekey) {
          coursekey
          title
          published
        }
      }
    `,
    UPDATE_COURSE: gql`
      mutation UpdateCourse(
        $coursekey: Int!
        $title: String!
        $description: String
        $tags: String
        $authors: [CourseAuthorInput]!
        $displaySettings: CourseDisplaySettingsInput
      ) {
        updateCourse(
          coursekey: $coursekey
          title: $title
          description: $description
          tags: $tags
          authors: $authors
          displaySettings: $displaySettings
        ) {
          coursekey
          title
          description
          created
        }
      }
    `,
    SAVE_LESSON: gql`
      mutation SaveLesson(
        $coursekey: Int!
        $lessonkey: Int!
        $version: Int!
        $type: LessonType
        $title: String!
        $description: String
        $tags: String
        $displaySettings: LessonDisplaySettingsInput
        $behaviorSettings: LessonBehaviorSettingsInput
        $xApiSettings: XApiSettingsInput
        $certificate: CertificateSettingsInput
      ) {
        saveLesson(
          coursekey: $coursekey
          lessonkey: $lessonkey
          version: $version
          type: $type
          title: $title
          description: $description
          tags: $tags
          displaySettings: $displaySettings
          behaviorSettings: $behaviorSettings
          xApiSettings: $xApiSettings
          certificate: $certificate
        ) {
          coursekey
          lessonkey
          title
          description
          tags
          type
          displaySettings {
            background {
              type
              value
              containerColor
            }
            icon {
              type
              value
            }
            palette {
              main
              dark
              contrastText
            }
          }
          behaviorSettings {
            showAllVersions
            lockCourse
            editAllVersions
            completionType
            completionThreshold
            multipleAttempts
          }
          xApiSettings {
            activityId
            activityName
            launchPage
          }
          version {
            value
            published
            live
            liveVersion
          }
          position
          required
          certificate {
            expiry
            media
            lifetime
            expires
          }
        }
      }
    `,
    ARCHIVE_LESSON: gql`
      mutation ArchiveLesson($lessonkey: Int!) {
        archiveLesson(lessonkey: $lessonkey) {
          modulekey
        }
      }
    `,
    RESTORE_LESSONS: gql`
      mutation RestoreLessons($lessonkeys: [Int]!) {
        restoreLessons(lessonkeys: $lessonkeys) {
          modulekey
        }
      }
    `,
    DELETE_LESSON: gql`
      mutation DeleteLesson($lessonkey: Int!) {
        deleteLesson(lessonkey: $lessonkey)
      }
    `,
    LOCK_COURSE: gql`
      mutation LockCourse($coursekey: Int!, $lessonkey: Int!, $lock: Boolean!) {
        lockCourse(coursekey: $coursekey, lessonkey: $lessonkey, lock: $lock) {
          modulekey
        }
      }
    `,
    SAVE_LESSON_COMPONENTS: gql`
      mutation SaveLessonComponents(
        $lessonkey: Int!
        $lessonVersion: Int!
        $authorkey: Int!
        $updatedComponents: [LessonComponentInput!]!
        $deletedComponents: [LessonComponentInput!]!
        $sortedComponents: [LessonComponentPosition!]!
      ) {
        saveLessonComponents(
          lessonkey: $lessonkey
          lessonVersion: $lessonVersion
          authorkey: $authorkey
          updatedComponents: $updatedComponents
          deletedComponents: $deletedComponents
          sortedComponents: $sortedComponents
        )
      }
    `,
    CREATE_AUTHOR: gql`
      mutation CreateAuthor($userkey: Int!) {
        createAuthor(userkey: $userkey) {
          domainkey
          userkey
          author
        }
      }
    `,
    DELETE_AUTHOR: gql`
      mutation DeleteAuthor($userkey: Int!, $masterAuthorDelete: Boolean) {
        deleteAuthor(userkey: $userkey, masterAuthorDelete: $masterAuthorDelete)
      }
    `,
    PUBLISH_COURSE_EDIT: gql`
      mutation PublishCourseEdit(
        $coursekey: Int!
        $lessons: [CourseEditLessonInput]!
      ) {
        publishCourseEdit(coursekey: $coursekey, lessons: $lessons) {
          lessonkey
          position
        }
      }
    `,
    EDIT_COURSE_CREATE_LESSON: gql`
      mutation EditCourseCreateLesson(
        $coursekey: Int!
        $type: LessonType
        $title: String!
        $description: String
        $tags: String
        $displaySettings: LessonDisplaySettingsInput
        $behaviorSettings: LessonBehaviorSettingsInput
        $xApiSettings: XApiSettingsInput
        $certificate: CertificateSettingsInput
      ) {
        editCourseCreateLesson(
          coursekey: $coursekey
          type: $type
          title: $title
          description: $description
          tags: $tags
          displaySettings: $displaySettings
          behaviorSettings: $behaviorSettings
          xApiSettings: $xApiSettings
          certificate: $certificate
        )
      }
    `,
    CLONE_LESSON: gql`
      mutation CloneLesson($coursekey: Int!, $lessonkey: Int!, $title: String) {
        cloneLesson(coursekey: $coursekey, lessonkey: $lessonkey, title: $title)
      }
    `,
    SAVE_COMPONENT_TEMPLATE: gql`
      mutation SaveComponentTemplate(
        $title: String!
        $componentKeys: [Int]!
        $description: String
      ) {
        saveComponentTemplate(
          title: $title
          componentKeys: $componentKeys
          description: $description
        ) {
          templatekey
        }
      }
    `,
    RENAME_COMPONENT_TEMPLATE: gql`
      mutation RenameComponentTemplate(
        $templatekey: Int!
        $title: String!
        $description: String
      ) {
        renameComponentTemplate(
          templatekey: $templatekey
          title: $title
          description: $description
        ) {
          templatekey
        }
      }
    `,
    DELETE_COMPONENT_TEMPLATE: gql`
      mutation DeleteComponentTemplate($templatekey: Int!) {
        deleteComponentTemplate(templatekey: $templatekey)
      }
    `,
    DELETE_LOCK: gql`
      mutation DeleteLock($modulekey: Int!) {
        deleteLock(modulekey: $modulekey)
      }
    `,
    CREATE_LINK: gql`
      mutation LessonLink($modulekey: Int!, $permaLink: Boolean) {
        lessonLink(modulekey: $modulekey, permaLink: $permaLink) {
          linkkey
          created
          expires
        }
      }
    `,
    DELETE_LINK: gql`
      mutation DeleteLink($linkkey: String!) {
        deleteLink(linkkey: $linkkey)
      }
    `
  }
};

export default GraphQL;
